/**
 * Upload transport — XHR with a STALL timer.
 *
 * ── Why this file exists (root cause, F39 / N17) ───────────────────────────
 * Every multipart upload in three apps used to be governed by the general
 * request timeout: `postForm` resolved its budget as
 *
 *     options.timeout ?? this.config.timeout ?? 60000
 *
 * and `createSdkConfig` ALWAYS populates `config.timeout`, so the `?? 60000`
 * was unreachable and the real budget was `DEFAULT_TIMEOUT` = 30 s. That is a
 * **wall-clock deadline measured from the start of the request**, not a stall
 * detector: a perfectly healthy 40 MB upload on a slow-but-working connection
 * is aborted at 30 s having transferred 29 s worth of bytes. Raising the number
 * does not fix it — it only moves the cliff, which is why production shows the
 * failure rate tracking SIZE (>50 MB fails 4 times in 5) rather than tracking
 * network quality.
 *
 * The correct budget for a transfer is a STALL window: "no progress for N
 * seconds" ends it, and an upload that is still moving bytes is never cut off
 * however long it legitimately takes. Measuring progress requires
 * `XMLHttpRequest` — `fetch` cannot report upload progress in any browser or in
 * React Native, and its `AbortSignal` can only express a deadline.
 *
 * The XHR-with-progress implementation was NOT invented here: it already
 * existed in business-web's company-document uploader (`xhr.upload.onprogress`
 * driving a percentage). It is lifted into the SDK so there is one
 * implementation rather than two, and so the mobility uploader — which had no
 * timeout of any kind — gets the stall window too.
 */

import { UPLOAD_TIMEOUT_MS } from '@tonnie/foundation';

import { TonnieApiError, apiErrorFrom } from './api-error';

/**
 * Milliseconds of NO upload progress before a transfer is abandoned.
 *
 * Defaulted from `UPLOAD_TIMEOUT_MS` in `@tonnie/foundation`, which has sat in
 * the repo with zero consumers. Its 120 s was written as a wall-clock budget;
 * as a stall window it is generous on purpose — a mobile radio handover or a
 * VPN reconnect can silently eat a minute without the transfer being dead, and
 * the cost of waiting is a spinner while the cost of cutting early is the
 * orphan row this whole wave exists to stop creating.
 */
export const DEFAULT_UPLOAD_STALL_MS = UPLOAD_TIMEOUT_MS;

export interface UploadProgress {
  /** Bytes sent so far. */
  loaded: number;
  /** Total bytes to send; `0` when the body length is not computable. */
  total: number;
  /** 0–1. `0` while the total is unknown. */
  fraction: number;
}

export interface XhrUploadRequest {
  method: 'POST' | 'PUT';
  /** Absolute URL. The caller has already resolved base URL + query. */
  url: string;
  body: FormData | Blob | ArrayBufferView | ArrayBuffer | string;
  headers?: Record<string, string>;
  /** Override the stall window. `0` is rejected — an unbounded upload is a leak. */
  stallTimeoutMs?: number;
  onProgress?: (progress: UploadProgress) => void;
  /** Caller-owned cancellation (component unmount, user "Cancel"). */
  signal?: AbortSignal;
  /** `'json'` parses the body, `'none'` discards it (S3 PUT answers empty). */
  responseType?: 'json' | 'none';
}

/**
 * The transfer made no progress for the whole stall window. (F39 / N17)
 *
 * A CLASS, not a code to string-compare: the pitch recorder tells the citizen
 * "your connection stopped sending" for this and "the file is too large" for
 * {@link UploadTooLargeError}, and a caller distinguishes them with
 * `instanceof`. Status 408 keeps the shape of the fetch-path timeout, so
 * `isTimeout` covers both; the code tells them apart (a STALL is a dead
 * connection, a TIMEOUT a deadline we imposed ourselves).
 */
export class UploadStalledError extends TonnieApiError {
  constructor(stallMs: number, loaded: number, total: number) {
    super({
      message: `Upload stalled — no data sent for ${Math.round(stallMs / 1000)}s`,
      status: 408,
      code: 'UPLOAD_STALLED',
      details: { stallTimeoutMs: stallMs, bytesSent: loaded, bytesTotal: total },
    });
    this.name = 'UploadStalledError';
    Object.setPrototypeOf(this, UploadStalledError.prototype);
  }
}

/**
 * The receiving end refused the body as too large — a 413 from the API (the
 * upload proxy's size limit, a reverse proxy's body cap), or S3's own
 * `EntityTooLarge`, which it answers as a 400 with an XML body. (F39 / N17)
 */
export class UploadTooLargeError extends TonnieApiError {
  /** Built from the parsed refusal, so a server-supplied code and message survive. */
  constructor(refusal: TonnieApiError) {
    super({
      message: refusal.message,
      status: refusal.status,
      code: refusal.code ?? 'UPLOAD_TOO_LARGE',
      details: refusal.details,
      data: refusal.data,
    });
    this.name = 'UploadTooLargeError';
    Object.setPrototypeOf(this, UploadTooLargeError.prototype);
  }
}

/** S3 (and MinIO) name an oversize PUT in the XML error body, under a 400. */
function isStorageEntityTooLarge(status: number, rawBody: string): boolean {
  return status === 400 && rawBody.includes('<Code>EntityTooLarge</Code>');
}

/**
 * Send `body` and resolve when the server answers 2xx.
 *
 * The stall timer is armed when the request is sent and **re-armed on every
 * upload progress event**. It is re-armed once more when the body has been
 * fully flushed, so a server that accepts the bytes and then never answers is
 * also bounded — without that, `upload.onprogress` stops firing at 100 % and
 * the request would hang forever on a silent backend.
 */
export function xhrUpload<T>(request: XhrUploadRequest): Promise<T> {
  const stallMs = request.stallTimeoutMs ?? DEFAULT_UPLOAD_STALL_MS;
  if (!Number.isFinite(stallMs) || stallMs <= 0) {
    return Promise.reject(
      new TonnieApiError({
        message: `Invalid upload stall window: ${String(request.stallTimeoutMs)}`,
        status: 0,
        code: 'INVALID_UPLOAD_TIMEOUT',
      })
    );
  }

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;
    let loaded = 0;
    let total = 0;

    const clearStall = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearStall();
      if (request.signal) request.signal.removeEventListener('abort', onExternalAbort);
      fn();
    };

    const armStall = () => {
      clearStall();
      timer = setTimeout(() => {
        // Abort FIRST so the socket is released, then reject. `xhr.abort()`
        // fires `onabort`, which is why `settled` is checked there.
        const err = new UploadStalledError(stallMs, loaded, total);
        finish(() => {
          try {
            xhr.abort();
          } catch {
            /* an already-finished xhr throws on abort in some engines */
          }
          reject(err);
        });
      }, stallMs);
    };

    function onExternalAbort() {
      finish(() => {
        try {
          xhr.abort();
        } catch {
          /* see above */
        }
        reject(
          new TonnieApiError({
            message: 'Upload cancelled',
            status: 0,
            code: 'UPLOAD_CANCELLED',
          })
        );
      });
    }

    if (request.signal) {
      if (request.signal.aborted) {
        onExternalAbort();
        return;
      }
      request.signal.addEventListener('abort', onExternalAbort);
    }

    xhr.open(request.method, request.url, true);

    for (const [key, value] of Object.entries(request.headers ?? {})) {
      xhr.setRequestHeader(key, value);
    }

    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (settled) return;
      loaded = event.loaded;
      total = event.lengthComputable ? event.total : 0;
      // THE line this whole file is for: progress resets the budget, so a slow
      // upload that is still moving is never cut off.
      armStall();
      request.onProgress?.({
        loaded,
        total,
        fraction: total > 0 ? loaded / total : 0,
      });
    };

    // Body fully flushed — the clock now covers the server's response instead.
    xhr.upload.onload = () => {
      if (!settled) armStall();
    };

    xhr.onload = () => {
      const status = xhr.status;
      if (status >= 200 && status < 300) {
        finish(() => {
          if (request.responseType === 'none') {
            resolve(undefined as T);
            return;
          }
          const text = xhr.responseText;
          if (!text) {
            resolve(null as T);
            return;
          }
          try {
            resolve(JSON.parse(text) as T);
          } catch {
            // A 2xx with an unparseable body is not an error for callers that
            // do not read it (S3), but it IS one for callers that do.
            reject(
              new TonnieApiError({
                message: 'Upload succeeded but the response body was not JSON',
                status,
                code: 'MALFORMED_RESPONSE',
                details: { bodyPreview: text.slice(0, 200) },
              })
            );
          }
        });
        return;
      }

      let parsed: unknown;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        // S3 answers XML, platform-api behind a proxy can answer HTML. Keep the
        // raw text so the error carries something a human can act on.
        parsed = { message: (xhr.responseText || '').slice(0, 300) };
      }
      const refusal = apiErrorFrom(status, xhr.statusText, parsed);
      finish(() =>
        reject(
          status === 413 || isStorageEntityTooLarge(status, xhr.responseText || '')
            ? new UploadTooLargeError(refusal)
            : refusal
        )
      );
    };

    xhr.onerror = () => {
      finish(() =>
        reject(
          new TonnieApiError({
            message: 'Network request failed — unable to reach the server',
            status: 0,
            code: 'NETWORK_ERROR',
            details: { url: request.url, method: request.method },
          })
        )
      );
    };

    xhr.onabort = () => {
      // Reached only when something outside this function aborted the request;
      // our own two abort paths have already settled the promise.
      finish(() =>
        reject(
          new TonnieApiError({
            message: 'Upload aborted',
            status: 0,
            code: 'UPLOAD_CANCELLED',
          })
        )
      );
    };

    armStall();
    xhr.send(request.body as XMLHttpRequestBodyInit);
  });
}
